import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom'; // ✅ Added useNavigate
import axios from 'axios';
import './InstructorProfile.css';
import api from '../../api';
import { getProfilePictureUrl } from '../../utils/apiHelper';
import defaultProfilePic from '../../assets/defaultProfilePic.png';
import { useAuth } from '../../context/AuthContext';

const InstructorProfile = () => {
  const { id } = useParams();
  const { token } = useAuth();
  const navigate = useNavigate(); // ✅ Initialize navigate
  const [instructor, setInstructor] = useState(null);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchInstructorDetails = async () => {
      try {
        const response = await axios.get(api.getInstructorDetailsWithCourses(id), {
          headers: { Authorization: `Bearer ${token}` },
        });

        // console.log("Instructor API response:", response.data);

        const totalLearners = response.data.courses.reduce(
          (sum, course) => sum + (course.enrolledStudents?.length || 0),
          0,
        );

        setInstructor({
          ...response.data.instructor,
          totalLearners,
        });

        setCourses(response.data.courses || []);

        // Fetch total reviews
        const reviewsResponse = await axios.get(api.getInstructorReviews(id), {
          headers: { Authorization: `Bearer ${token}` },
        });
        setInstructor(prev => ({ ...prev, totalReviews: reviewsResponse.data.totalReviews }));
      } catch (error) {
        console.error('Error fetching instructor details:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchInstructorDetails();
  }, [id, token]);

  if (loading) return <div className="inp-loading">Loading instructor details...</div>;
  if (!instructor) return <div className="inp-error">Instructor not found.</div>;

  return (
    <div className="inp-profile-container">
      <button
        className="pro-info-icon-btn"
        onClick={() => navigate(-1)}
      >
        &times;
      </button>
      {/* Header */}
      <div className="inp-header">
        <div className="inp-header-left">
          <p className="inp-title">Instructor</p>
          <h1>{instructor.name}</h1>
          <p className="inp-bio">{instructor.bio || 'No bio available.'}</p>
        </div>
        <div className="inp-header-right">
          <img
            src={getProfilePictureUrl(instructor.profilePicture, api.img, defaultProfilePic, instructor.name)}
            alt={instructor.name}
            className="inp-profile-pic"
          />
          <div className="inp-social-icons">
            {instructor.socialLinks?.linkedin && (
              <a href={instructor.socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                <i className="fab fa-linkedin"></i>
              </a>
            )}
            {instructor.socialLinks?.twitter && (
              <a href={instructor.socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                <i className="fab fa-twitter"></i>
              </a>
            )}
            {instructor.socialLinks?.youtube && (
              <a href={instructor.socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                <i className="fab fa-youtube"></i>
              </a>
            )}
          </div>
        </div>
      </div>

      {/* Stats */}
      <div className="inp-stats">
        <div className="inp-stat-card">
          <p><strong>{instructor.totalLearners || 0} Total Learners</strong></p>
        </div>
        <div className="inp-stat-card">
          <p><strong>{instructor.totalReviews || 0} Reviews</strong></p>
        </div>
      </div>

      {/* About */}
      <div className="inp-about">
        <h2>About Me</h2>
        <p>{instructor.aboutMe || 'No additional information available.'}</p>
      </div>

      {/* Courses */}
      <div className="inp-courses">
        <h2>My Courses ({courses.length})</h2>
        <div className="inp-courses-grid">
          {courses.map((course) => (
            <div
              key={course._id}
              className="inp-course-card"
              onClick={() => navigate(`/courses/${course._id}`)} // ✅ Corrected syntax
              style={{ cursor: 'pointer' }}
            >
              <img
                src={course.image ? `${api.img}${course.image}` : defaultProfilePic}
                alt={course.title}
                className="inp-course-image"
              />
              <h3>{course.title}</h3>
              <p>
                ⭐ {course.averageRating || '0.0'} (
                {course.totalRatings !== undefined
                  ? course.totalRatings
                  : (course.ratings ? course.ratings.length : 0)} ratings)
              </p>
              <p>{course.enrolledStudents?.length || 0} Students</p>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

export default InstructorProfile;
